import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { chatApi } from '../api';
import { streamSSE } from '../auth_api';
import { CHAT_SEED } from '../data';

/** Chat streaming + persistence for a single project.
 *
 *  Returns the messages array (`chat`), `sendChat` (SSE-driven), `clearChat`,
 *  and the live in-flight stream buffer (`streamingChatText`, `chatBusy`).
 *
 *  @param {object} project        – current project from usePW
 *  @param {string|null} activeChapterId – chapter the author is currently editing;
 *                                         forwarded to the backend to sharpen context */
export function useChatStream(project, activeChapterId = null) {
  const [chat, setChat] = useState(CHAT_SEED);
  const [chatBusy, setChatBusy] = useState(false);
  const [streamingChatText, setStreamingChatText] = useState('');

  const sendChat = useCallback(async (text) => {
    if (!project) { toast.error('No project loaded'); return; }
    setChatBusy(true);
    setStreamingChatText('');
    const optimisticUserId = `local-${Date.now()}`;
    setChat((prev) => [...prev, { id: optimisticUserId, from: 'user', text }]);

    let serverUser = null;
    let serverClaude = null;
    let buf = '';

    // Pass the active chapter so the backend can surface it with more context
    const payload = { text };
    if (activeChapterId) payload.active_chapter_id = activeChapterId;

    try {
      await streamSSE(`/stream/projects/${project.id}/chat`,
        payload,
        {
          onEvent: (ev) => {
            if (ev.event === 'meta') {
              if (ev.data.model_fallback && ev.data.requested_model_id !== ev.data.model_id) {
                toast.warning(
                  `This chat replied using ${ev.data.model_id} because ${ev.data.requested_model_id} is currently unavailable. Choose another model in Chat With.`,
                  { duration: 6000 }
                );
              }
            } else if (ev.event === 'user') {
              serverUser = ev.data;
            } else if (ev.event === 'delta') {
              buf += ev.data.text;
              setStreamingChatText(buf);
            } else if (ev.event === 'done') {
              serverClaude = ev.data;
              window.dispatchEvent(new CustomEvent('cr-cache-status', {
                detail: {
                  read:      ev.data.cache_read  || 0,
                  write:     ev.data.cache_write || 0,
                  saved_usd: ev.data.saved_usd   || 0,
                },
              }));
            } else if (ev.event === 'error') {
              toast.error(`AI error: ${ev.data.message}`);
            }
          },
        });
    } catch {
      toast.error('Connection error.');
    }

    setChat((prev) => {
      const without = prev.filter((m) => m.id !== optimisticUserId);
      const userMsg = serverUser
        ? { id: serverUser.id, from: 'user', text: serverUser.text }
        : { id: optimisticUserId, from: 'user', text };
      const claudeMsg = serverClaude
        ? { id: serverClaude.id, from: 'claude', text: serverClaude.text }
        : null;
      return claudeMsg ? [...without, userMsg, claudeMsg] : [...without, userMsg];
    });
    setStreamingChatText('');
    setChatBusy(false);
  }, [project, activeChapterId]);

  const clearChat = useCallback(async () => {
    if (!project) return;
    try {
      await chatApi.clear(project.id);
      setChat([]);
    } catch (err) {
      console.warn('[store] clearChat failed', err);
      toast.error('Could not clear conversation.');
    }
  }, [project]);

  return { chat, setChat, chatBusy, streamingChatText, sendChat, clearChat };
}
